import {
    DATING_EVENTBRITE_SUB_CATEGORY,
    EVENTBRITE_CATEGORY_TO_REFINEMENT,
    EVENTBRITE_REFINEMENT_TO_CATEGORY,
    FORMATTED_SUBCATEGORY_MAP,
    getParentCategoryFromSubCategory,
    isSubCategory,
    NIGHTLIFE_EVENTBRITE_CATEGORY,
    SUBCATEGORY_REFINEMENT_TO_KEY,
} from '@eventbrite/categories';
import { OrganicEventSearchFiltersApiPayload } from '../../../api/searchOrganicEvents';
import { DateRange, DateType } from '../../../constants/dates';
import { FormattedLocationResponse } from '../api';
import { CategoryBrowseRouteMatch } from '../CategoryBrowseProviders';
import { DATING_SUBCATEGORY_URL_PARAM_TO_KEY } from '../components/CategoryFilterBar/constants';
import { CATEGORY_URL_PARAM_TO_KEY } from '../constants/nightLife';
import { BUCKET_URL_PARAM_TO_KEY } from '../constants/seasonal';

const TAG_TO_CATEGORY_LINK: { [key: string]: any } = {
    [DATING_EVENTBRITE_SUB_CATEGORY]: DATING_SUBCATEGORY_URL_PARAM_TO_KEY,
    [NIGHTLIFE_EVENTBRITE_CATEGORY]: CATEGORY_URL_PARAM_TO_KEY,
};

/**
 * Grabs the appropriate filters from the URL. These include:
 *
 * tag: The active tag to filter against
 * date: String representation of the date (today, tomorrow, etc)
 * start_date: Custom date range in the YYYY-MM-DD format
 * end_date: Custom date range in the YYYY-MM-DD format
 *
 * @param params URL Params
 * @returns
 */
export const parseEventSearchFromUrlParams = (
    params: URLSearchParams,
    match: CategoryBrowseRouteMatch,
) => {
    let activeTag: string = params.get('tag') || '';
    if (!activeTag) {
        activeTag = EVENTBRITE_REFINEMENT_TO_CATEGORY[match.params.category];
        if (match.params.subcategory) {
            activeTag =
                SUBCATEGORY_REFINEMENT_TO_KEY[activeTag][
                    match.params.subcategory
                ];
        }
    }
    const date = (params.get('date') as DateType) || 'current_future';
    const customDate =
        params.has('start_date') && params.has('end_date')
            ? ({
                  from: params.get('start_date') || undefined,
                  to: params.get('end_date') || undefined,
              } as DateRange)
            : undefined;

    const category =
        params.has('category') && TAG_TO_CATEGORY_LINK[activeTag]
            ? TAG_TO_CATEGORY_LINK[activeTag][params.get('category') || '']
            : undefined;
    const bucket = params.has('bucket')
        ? BUCKET_URL_PARAM_TO_KEY[params.get('bucket') || '']
        : undefined;

    return {
        activeTag,
        date,
        customDate,
        category,
        bucket,
    };
};

/**
 * Generates an appropriate path for relative linking to browse pages.
 * Utilized on discover pages where we want to link to a browse page
 * with a specific tag, without copying over URL param information.
 */
export const generateBrowsePath = (
    tag?: string,
    locationSlug?: string,
    localeCode?: string,
) => {
    const path = `${localeCode}/b/${locationSlug}/`;
    if (!tag) {
        return path;
    } else if (isSubCategory(tag)) {
        const parentCategory = getParentCategoryFromSubCategory(tag);
        const categoryRefinement =
            EVENTBRITE_CATEGORY_TO_REFINEMENT[parentCategory];
        const { refinement: subcategoryRefinement } =
            FORMATTED_SUBCATEGORY_MAP[tag];

        return `${path}${categoryRefinement}/${subcategoryRefinement}/`;
    } else {
        const categoryRefinement = EVENTBRITE_CATEGORY_TO_REFINEMENT[tag];
        return `${path}${categoryRefinement}/`;
    }
};

/**
 * Generates an appropriate browse URL for relative
 * linking across pages. Will parameterize tag, date,
 * and location information.
 */
export const generateBrowseUrl = (
    currentQuery: OrganicEventSearchFiltersApiPayload,
    location: FormattedLocationResponse,
    localeCode?: string,
) => {
    const params = new URLSearchParams();
    const tag = currentQuery.tags?.[0];
    const path = generateBrowsePath(tag, location.slug, localeCode);

    if (currentQuery.date_range?.from && currentQuery.date_range.to) {
        params.set('start_date', currentQuery.date_range.from);
        params.set('end_date', currentQuery.date_range.to);
    }

    return `${path}?${params.toString()}`;
};
